import { Badge, Flex, Text } from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { WebSocketHelper } from 'classes/helpers/web-socket.helper';
import { CommonCallout } from 'components/common/callouts';
import { ErrorBoundary } from 'components/common/error-boundary';
import { StreamingProvider } from 'components/machine/camera-stream-viewer/context';
import { MsDialog } from 'components/sections/admin-portal/machines/dialogs/ms-dialog';
import {
  RemoteVisionCalibrationCameraCalibration,
  SendRemoteCalRequest,
} from 'components/sections/admin-portal/machines/editor/remote-vision-calibration.tab/camera-calibration';
import { RemoteVisionCalibrationOverview } from 'components/sections/admin-portal/machines/editor/remote-vision-calibration.tab/overview';
import { RemoteVisionCalibrationPlateDistance } from 'components/sections/admin-portal/machines/editor/remote-vision-calibration.tab/plate-distance';
import useVisionRemoteStore, {
  DEFAULT_CAMERA_CONFIG,
} from 'components/sections/admin-portal/machines/editor/remote-vision-calibration.tab/store/useVisionRemoteStore';
import { RemoteVisionCalibrationVerification } from 'components/sections/admin-portal/machines/editor/remote-vision-calibration.tab/verification';
import { useMachinesPortalStore } from 'components/sections/admin-portal/machines/store/use-machines-portal-store';
import {
  getMachineStatusColor,
  getMachineStatusDisplay,
} from 'enums/admin-portal';
import { VisionRemoteStage } from 'enums/calibration';
import { t } from 'i18next';
import { FT_TO_INCHES } from 'lib_ts/classes/math.utilities';
import { CameraSource, WsMsgType } from 'lib_ts/enums/machine-msg.enum';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { VisionCalibrationCameraType } from 'lib_ts/enums/vision.enums';
import { IForwardWsMsg } from 'lib_ts/interfaces/machine-msg/i-forward-ws';
import { IMachineStateCurrent } from 'lib_ts/interfaces/machine-msg/i-machine-state';
import { IReadyMsg } from 'lib_ts/interfaces/machine-msg/i-ready';
import { IVisionRemoteCalibrationResponse } from 'lib_ts/interfaces/machine-msg/vision/i-remote-calibration';
import { useCallback, useEffect, useState } from 'react';
import { AdminMachinesService } from 'services/admin/machines.service';

// if the machine's px is +/- half an inch from center, consider it close enough for safe calibration
const CENTERED_PX_THRESHOLD_IN = 0.5;

const Stage = ({
  sendRemoteCalRequest,
}: {
  sendRemoteCalRequest: SendRemoteCalRequest;
}) => {
  const stage = useVisionRemoteStore((s) => s.stage);
  const machine = useMachinesPortalStore((s) => s.machine);

  switch (stage) {
    case VisionRemoteStage.Overview:
      return <RemoteVisionCalibrationOverview />;
    case VisionRemoteStage.PlateDistance:
      if (!machine) {
        return null;
      }
      return <RemoteVisionCalibrationPlateDistance machine={machine} />;
    case VisionRemoteStage.LeaderCalibration:
      return (
        <RemoteVisionCalibrationCameraCalibration
          cameraType={VisionCalibrationCameraType.leader}
          sendRemoteCalRequest={sendRemoteCalRequest}
        />
      );
    case VisionRemoteStage.FollowerCalibration:
      return (
        <RemoteVisionCalibrationCameraCalibration
          cameraType={VisionCalibrationCameraType.follower}
          sendRemoteCalRequest={sendRemoteCalRequest}
        />
      );
    case VisionRemoteStage.Verification:
      if (!machine) {
        return null;
      }

      return (
        <RemoteVisionCalibrationVerification
          machine={machine}
          sendRemoteCalRequest={sendRemoteCalRequest}
        />
      );
    default:
      return null;
  }
};

const PositionSafetyCallout = () => {
  const machine = useMachinesPortalStore((s) => s.machine);
  const lastMachineStatus = useMachinesPortalStore((s) => s.lastMachineStatus);
  const setLastMachineStatus = useMachinesPortalStore(
    (s) => s.setLastMachineStatus
  );

  const [showWarning, setShowWarning] = useState<boolean>(false);
  const [ms, setMs] = useState<IMachineStateCurrent>();
  const [r2fData, setR2fData] = useState<IReadyMsg>();
  const [msDialog, setMsDialog] = useState<number>();

  // WS subscription to this machine's ms & r2f messages
  useEffect(() => {
    if (!machine?.machineID) {
      return;
    }

    const onMessage = (event: CustomEvent) => {
      const { type } = event.detail;

      switch (type) {
        case WsMsgType.M2U_MsCurrent: {
          const data = event.detail.data as IMachineStateCurrent;
          console.log('*** ms data', data);

          setMs(data);

          // if px is within 0.5 inches of center, consider it close enough to centered for safe calibration
          const pxIn = data.px * FT_TO_INCHES;
          if (Math.abs(pxIn) < CENTERED_PX_THRESHOLD_IN) {
            setShowWarning(false);
            return;
          }

          setShowWarning(true);
          break;
        }
        case WsMsgType.M2U_ReadyToFire: {
          const data = event.detail.data as IReadyMsg;

          setR2fData(data);

          if (!data.machine_status) {
            return;
          }

          setLastMachineStatus(data.machine_status);
          break;
        }
        default:
          break;
      }
    };

    WebSocketHelper.on(WsMsgType.S2U_ForwardWs, onMessage);

    return () => {
      WebSocketHelper.remove(WsMsgType.S2U_ForwardWs, onMessage);
    };
  }, [machine?.machineID]);

  const showStatus = !!machine?.connected && !!lastMachineStatus;

  if (!machine) {
    return null;
  }

  return (
    <>
      <Flex
        gap={RADIX.FLEX.GAP.XS}
        align="center"
        position="absolute"
        top={showWarning ? '8px' : '0'}
        right={showWarning ? '8px' : '0'}
        onClick={() => setMsDialog(Date.now())}
        className="cursor-pointer"
      >
        <Badge
          color={machine.connected ? RADIX.COLOR.SUCCESS : RADIX.COLOR.DANGER}
        >
          {machine.connected ? t('admin.online') : t('admin.offline')}
        </Badge>
        {showStatus && (
          <Badge color={getMachineStatusColor(lastMachineStatus)}>
            {getMachineStatusDisplay(lastMachineStatus)}
          </Badge>
        )}
      </Flex>
      {showWarning && (
        <CommonCallout
          content={
            <Flex direction="column" gap={RADIX.FLEX.GAP.XS}>
              <Text>WARNING: Machine is not currently centered.</Text>
              <Text>
                Please ask the operator to lower the machine before starting
                calibration, to ensure accurate tracking and safe operation.
              </Text>
              <Text weight="bold">
                If the operator is not available and it is safe to do so,
                impersonate the user and click Lower.
              </Text>
            </Flex>
          }
          color={RADIX.COLOR.DANGER}
          style={{
            flexShrink: 0,
          }}
        />
      )}
      {msDialog && (
        <MsDialog
          key={msDialog}
          identifier="R2FStatusDialog"
          onClose={() => setMsDialog(undefined)}
          ms={ms}
          readyData={r2fData?.ready_comps ?? r2fData?.data}
          loading={false}
          machineID={machine.machineID}
        />
      )}
    </>
  );
};

export const RemoteVisionCalibration = () => {
  const machine = useMachinesPortalStore((s) => s.machine);

  const saving = useVisionRemoteStore((s) => s.saving);

  const vrStoreState = useVisionRemoteStore.getState();

  const sendRemoteCalRequest = useCallback<SendRemoteCalRequest>(
    async (payload) => {
      try {
        console.log('Sending remote cal request:', payload);
        if (!machine?.machineID) {
          return;
        }

        await AdminMachinesService.getInstance().sideloadWebSocket(
          machine.machineID,
          WsMsgType.U2M_VisionRemoteCalibrationRequest,
          payload
        );
      } catch (e) {
        console.error(e);
      }
    },
    [machine?.machineID]
  );

  // wait for we get a response before showing any UI to avoid issues with stream starting before FW is ready
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const init = async () => {
      vrStoreState.setLoading(true);
      await sendRemoteCalRequest({
        step: 'start',
        leader: DEFAULT_CAMERA_CONFIG,
        follower: DEFAULT_CAMERA_CONFIG,
      });
    };

    init();

    return () => {
      sendRemoteCalRequest({
        step: 'stop',
        leader: DEFAULT_CAMERA_CONFIG,
        follower: DEFAULT_CAMERA_CONFIG,
      });
      vrStoreState.reset();
    };
  }, []);

  useEffect(() => {
    const callback = (event: CustomEvent) => {
      const fwdEvent = event.detail as IForwardWsMsg;

      if (fwdEvent.type !== WsMsgType.M2U_VisionRemoteCalibrationResponse) {
        return;
      }

      console.log('** Received Vision Remote Calibration Response:', fwdEvent);

      const data = fwdEvent.data as IVisionRemoteCalibrationResponse;

      vrStoreState.setLastResponse(data);
      vrStoreState.setLoading(false);

      setReady(true); // wait for the first response before showing UI

      if (!saving) {
        return;
      }

      // if we were waiting for a save response, reset all sliders back to 0
      vrStoreState.setSaving(false);
      vrStoreState.setLeaderSliders(DEFAULT_CAMERA_CONFIG);
      vrStoreState.setFollowerSliders(DEFAULT_CAMERA_CONFIG);

      NotifyHelper.success({
        message_md: 'Camera calibration settings saved successfully.',
      });
    };

    WebSocketHelper.on(WsMsgType.S2U_ForwardWs, callback);
    return () => {
      WebSocketHelper.remove(WsMsgType.S2U_ForwardWs, callback);
    };
  }, [saving]);

  if (!ready) {
    return null;
  }

  if (!machine) {
    return null;
  }

  return (
    <ErrorBoundary identifier="RemoteVisionCalibrationTab">
      <StreamingProvider
        machineID={machine.machineID}
        hwVersion={machine.hardware_version}
        defaultSource={CameraSource.STEREO_CHECK}
      >
        <Flex
          direction="column"
          gap={RADIX.FLEX.GAP.MD}
          height="100%"
          overflow="hidden"
          position="relative"
        >
          <PositionSafetyCallout />
          <Flex direction="column" flexGrow="1" overflow="hidden">
            <Stage sendRemoteCalRequest={sendRemoteCalRequest} />
          </Flex>
        </Flex>
      </StreamingProvider>
    </ErrorBoundary>
  );
};

export const RemoteVisionCalibrationTab = () => {
  const machine = useMachinesPortalStore((s) => s.machine);

  if (!machine?.connected) {
    return (
      <Flex direction="column" pt="1">
        <CommonCallout
          text={t('admin.machine-is-currently-offline').toString()}
          color={RADIX.COLOR.DANGER}
        />
      </Flex>
    );
  }

  return <RemoteVisionCalibration />;
};
